import { icons } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { type ModuleDefinition, POINT_LABEL } from "@/data/modules";

interface ModuleCardProps {
  module: ModuleDefinition;
  selected?: boolean;
  onToggle?: (id: string) => void;
  disabled?: boolean;
  helpSlug?: string;
}

const POINT_COLORS: Record<1 | 2 | 3, string> = {
  1: "bg-success/10 text-success border-success/20",
  2: "bg-primary/10 text-primary border-primary/20",
  3: "bg-accent/10 text-accent border-accent/20",
};

export function ModuleCard({ module, selected = false, onToggle, disabled = false, helpSlug }: ModuleCardProps) {
  const LucideIcon = icons[module.icon as keyof typeof icons] ?? icons["Box"];

  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle?.(module.id)}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "group relative w-full text-left rounded-xl border p-4 transition-all duration-200 shadow-card",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected
          ? "border-primary bg-primary/5 shadow-card-hover"
          : "border-border bg-card hover:border-primary/40 hover:shadow-card-hover",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      {/* Selection indicator */}
      <span
        className={cn(
          "absolute top-3 right-3 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors",
          selected
            ? "border-primary bg-primary"
            : "border-border bg-background group-hover:border-primary/50",
        )}
      >
        {selected && (
          <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3 text-primary-foreground">
            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>

      {/* Icon */}
      <div
        className={cn(
          "mb-3 inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
          selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary",
        )}
      >
        {LucideIcon && <LucideIcon size={18} strokeWidth={1.8} />}
      </div>

      {/* Name */}
      <p className="font-display font-semibold text-sm text-foreground leading-snug mb-1 pr-6">
        {module.name}
      </p>

      {/* Short description */}
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        {module.shortDescription}
      </p>

      {/* Bottom row: points badge + tags + help link */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={cn(
            "inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold",
            POINT_COLORS[module.points],
          )}
        >
          {POINT_LABEL[module.points]}
        </span>
        {module.tags.slice(0, 2).map((tag) => (
          <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
            {tag}
          </Badge>
        ))}
        {helpSlug && (
          <Link
            to={`/hilfe/${helpSlug}`}
            onClick={(e) => e.stopPropagation()}
            className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10 transition-colors"
          >
            📖 Anleitung
          </Link>
        )}
      </div>
    </button>
  );
}
